import Settings from "Data/Settings";
import { Elastic, Sine, TimelineLite, TimelineMax, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import {DWELL_DURATION} from "Data/Constants";
import { OutlineFilter } from "Filters/OutlineFilter";

export class LayoutButton extends HLF.Button {

    protected static readonly SCALE_NORMAL: number = 1.0;
    protected static readonly SCALE_OVER: number = 1.05;
    protected static readonly SCALE_DOWN: number = 0.95;

    public outlineFilter = new OutlineFilter(5, 0xffffff);

    public disabledTint: number | undefined = 0x696969;
    public noHover: boolean = false;

    protected _clickedSFX: string | undefined;

    protected dwellTimeline?: TimelineLite;
    protected isCursorDwelling: boolean = false;

    protected _subscriptions: any[] = [];

    protected _hintTimeline?: TimelineMax;
    protected isOver: boolean = false;
    protected readonly affectedBySettingsScale: boolean = true;
    private _originalScale: number = 1;

    constructor (
        pCallback: () => void,
        public hoverVO: string | string[],
        pClickedSFX?: string,
    ) {
        super(pCallback, undefined as any as string, undefined as any as string);

        this._visuals.filters = [this.outlineFilter];
        this.outlineFilter.enabled = false;

        this._clickedSFX = pClickedSFX;

        // for accessibility
        this.addListener(HLF.Input.Events.CLICK, (e)=>{
            this.onPointerDown(e);
            this.onPointerUp(e);
        });

        this._subscriptions.push(
            PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this)),
        );
    }

    public setLayoutTexture(texture: string, anchorX: number, anchorY: number) {
        texture = texture.replace(/\.png$|\.jpe?g$/, "");
        texture = texture.replace(/@2x$|@1x$/, "");
        this.name = texture;

        if (this._image === undefined) {
            this._image = HLF.PixiUtils.makeSprite(texture);
            this._enabledTexture = this._image.texture;
            this._visuals.addChild(this._image!);
        }
        else {
            this._image.texture = HLF.PixiUtils.getTexture(texture);
        }

        this._image.anchor.x = 0.5;
        this._image.anchor.y = 0.5;
        this._visuals.position.x = -this._image.width * (anchorX - 0.5);
        this._visuals.position.y = -this._image.height * (anchorY - 0.5);

        this.hitArea = this._image !== undefined ?
            new PIXI.Rectangle(
                -this._image.width * anchorX,
                -this._image.height * anchorY,
                this._image.width,
                this._image.height) :
            new PIXI.Rectangle(-50, -50, 100, 100);
    }

    public onLayout() {
        this._originalScale = this.scale.x;
        if (this.affectedBySettingsScale === true) {
            this.scale.set(Settings.buttonSizeMultiplier * this._originalScale);
        }
    }

    public pulse(scale: number = 1.15, interval: number = 1.5, startImmediately: boolean = true) {
        this._hintTimeline?.kill();
        this._originalScale = this.scale.x;
        this._hintTimeline = new TimelineMax({repeat: -1});
        this._hintTimeline
            .to(this, 0.15, {
                pixi: {
                    scale: this._originalScale * scale
                },
                ease: Sine.easeInOut,
            }, interval)
            .call(()=>this.enableOutline())
            .to(this, 1.0, {
                pixi: {
                    scale: this._originalScale,
                },
                ease: Elastic.easeOut,
            })
            .call(()=>{
                this._hintTimeline?.seek(0);
                if (this.isOver) {
                    this._hintTimeline?.pause();
                }
                else {
                    this.disableOutline();
                }
            })

        if (startImmediately) {
            this._hintTimeline.seek(interval);
        }
    }

    public stopPulse() {
        this._hintTimeline?.kill();
        this._hintTimeline?.clear();
        this._hintTimeline?.to(this, 0.15, {pixi: {scale: this._originalScale}, ease: Elastic.easeOut});
    }

    public setDisabledImage(pTexture: PIXI.Texture | string, pSheet?: string): void {
        super.setDisabledImage(pTexture, pSheet);
        this.disabledTint = undefined;
    }

    public destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]): void {
        super.destroy(pOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
        this._hintTimeline?.kill();
    }

    public onFocusBegin(): void {
        super.onFocusBegin();
        this.playKeyboardSelectVO();
        this.enableOutline();
    }

    public onFocusEnd(): void {
        super.onFocusEnd();
        this.disableOutline();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.playClickedSFX();
            this.simulateClick();
        }
        super.onFocusActivated();
    }

    public simulateClick(): void {
        const clickTimeline: TimelineLite = new TimelineLite();
        clickTimeline.call(this.scaleDown, undefined, this, 0);
        clickTimeline.call(this.enableOutline, undefined, this, 0);
        clickTimeline.call(() => {
            this.scaleOver();
            this.disableOutline();
        }, undefined, this, 0.15);
    }

    public scaleNormal(): void {
        TweenMax.to(this._visuals.scale, 0.15, {
            x: LayoutButton.SCALE_NORMAL,
            y: LayoutButton.SCALE_NORMAL,
            ease: Sine.easeInOut,
        });
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    public setInteractive(pInteractive: boolean) {
        super.setInteractive(pInteractive);
        if (pInteractive === false) {
            this.disableOutline();
            this.scaleNormal();
        }
        if (this._image) {
            if (pInteractive === false) {
                this.tintDisabled();
            }
            else {
                this.tintEnabled();
            }
        }
        if (this.isOver) {
            this.isOver = false;
        }
    }

    protected tintEnabled() {
        if (this.disabledTint !== undefined) {
            TweenLite.to(this._image, 0.15, {
                pixi: {
                    tint: 0xffffff,
                },
            });
        }
    }

    protected tintDisabled() {
        if (this.disabledTint !== undefined) {
            TweenLite.to(this._image, 0.15, {
                pixi: {
                    tint: this.disabledTint,
                }
            });
        }
    }

    protected enableOutline() {
        this.outlineFilter.enabled = true;
    }

    protected disableOutline() {
        this.outlineFilter.enabled = false;
    }

    protected scaleDown(): void {
        TweenMax.to(this._visuals.scale, 0.15, {
            x: LayoutButton.SCALE_DOWN,
            y: LayoutButton.SCALE_DOWN,
            ease: Sine.easeInOut,
        });
    }

    protected scaleOver(): void {
        TweenMax.to(this._visuals.scale, 0.15, {
            x: LayoutButton.SCALE_OVER,
            y: LayoutButton.SCALE_OVER,
            ease: Sine.easeInOut,
        });
    }

    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        if (this.noHover) {
            return;
        }
        super.onPointerOver(pEvent);
        this.playHoverVO();
        if (Settings.useDwellActivation) {
            this.beginDwell();
        }
        this.enableOutline();
        this.scaleOver();
        this.isOver = true;
    }

    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        super.onPointerDown(pEvent);
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        this.enableOutline();
        this.scaleDown();
    }

    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        if (this._eventData !== undefined) {
            this.playClickedSFX();
        }
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        if (this._eventData !== undefined && this._eventData.identifier === pEvent.data.identifier) {
            this.scaleOver();
        }
        super.onPointerUp(pEvent);
        if (HLF.PlatformUtils.isMobile()) {
            this.disableOutline();
        }
    }

    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        if (this.noHover) {
            return;
        }
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        super.onPointerOut(pEvent);
        this.scaleNormal();
        this.disableOutline();
        this.isOver = false;
        this._hintTimeline?.resume();
    }

    protected playHoverVO(): void {
        HLF.Application.instance.voiceover.playVO(this.hoverVO, { priority: -1, mode: HLF.PlayMode.Override });
    }

    protected playKeyboardSelectVO(): void {
        HLF.Application.instance.voiceover.stopVO();
        HLF.Application.instance.voiceover.playVO(this.hoverVO, { priority: 0, mode: HLF.PlayMode.Override });
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSFX, 1, false, HLF.AudioCategory.SFX));
        }
    }

    protected beginDwell() {
        this.isCursorDwelling = true;
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        this.dwellTimeline = new TimelineLite();
        this.dwellTimeline.call(()=>PubSub.publishSync(Topics.DWELL_BEGIN, this), undefined, undefined, 0);
        this.dwellTimeline.call(()=>this.completeDwell(), undefined, undefined, DWELL_DURATION);
    }

    protected cancelDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        if (this.isCursorDwelling) {
            this.isCursorDwelling = false;
            PubSub.publishSync(Topics.DWELL_CANCELLED, this);
        }
    }

    protected completeDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        PubSub.publishSync(Topics.DWELL_ACTIVATED, this);
        this.isCursorDwelling = false;
        this.onFocusActivated();
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        if (this.affectedBySettingsScale === true) {
            this.scale.set(pSettings.buttonSizeMultiplier * this._originalScale);
        }
    }
}
